import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import classnames from 'clsx';

import { Button } from 'ui-components/components/button';
import { noop } from 'ui-components/default-props/noop';
import { Tooltip } from 'ui-components/components/tooltip';

import { ModalPortal } from '../../../components/modal-portal';
import { MultiPackageModal } from '../../../components/modals/multi-package-modal';
import { MultiPackageModalDnD } from '../../../components/modals/multi-package-modal-dnd';
import { useModal } from '../../../hooks/use-modal';
import { useFulfillmentPlannerContext } from '../../context';
import { useShouldRequirePackageContentDescription } from '../../package-content-description';
import { useEnableMultiPackageItemAllocation } from '../../../feature-flags/hooks/use-enable-multi-package-item-allocation';
import { usePackingApplied } from '../../../orders/order-details/configure-shipment/fields/packing-recommend/use-packing-applied';
import Global from '../../../store/global-selectors';

import style from './style.module.css';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { messages } from './messages';

/**
 * @param {object} props
 * @param {string} [props.buttonClassName]
 * @param {string} [props.iconClassName]
 * @param {() => void} [props.onTrackOpenMultiPackageModal]
 * @returns {JSX.Element}
 */
const MultiPackageComponent = ({ buttonClassName, iconClassName, onTrackOpenMultiPackageModal = noop }) => {
  const { isOpen, open, close } = useModal();
  const {
    fulfillmentPlanId,
    hasInsuranceProviderSelected,
    onMultiPackageSave,
    packages: orderPackages,
    serviceIdAndSellerLabelProviderId,
    items: orderItems,
    packageItemAssignments,
  } = useFulfillmentPlannerContext();

  const packageCount = orderPackages?.length ?? 0;
  const supportsMultiPackage = useSelector((state) => {
    const service =
      Global.selectSellerServiceById(state, {
        serviceId: serviceIdAndSellerLabelProviderId?.serviceId,
      }) || {};
    return service.features?.supportsMultiPackage ?? false;
  });

  const shouldRequirePackageDescription = useShouldRequirePackageContentDescription();
  const isMultiPackageItemAllocationEnabled = useEnableMultiPackageItemAllocation();
  const { isPackingApplied } = usePackingApplied(fulfillmentPlanId);
  const suppressExtraEmptyPackage = isMultiPackageItemAllocationEnabled && isPackingApplied;

  const ModalComponent = isMultiPackageItemAllocationEnabled ? MultiPackageModalDnD : MultiPackageModal;

  const onSave = useCallback(
    (packages, assignments) => {
      onMultiPackageSave(packages, assignments);
      close();
    },
    [close, onMultiPackageSave],
  );

  const onClickMultiPackageButton = useCallback(() => {
    onTrackOpenMultiPackageModal();
    open();
  }, [onTrackOpenMultiPackageModal, open]);

  if (!supportsMultiPackage) {
    return null;
  }

  return (
    <>
      <Tooltip position='top'>
        <Button
          className={classnames(style['multi-package'], buttonClassName)}
          onClick={onClickMultiPackageButton}
          testId='multi-package-launcher'
          theme='unstyled'
        >
          <div className={style['icon-container']}>
            <FontAwesomeIcon className={classnames(iconClassName, style.icon)} icon={faPlus} />
          </div>
          {packageCount > 1 ? packageCount : ''}
        </Button>
        <TooltipContent>
          <FormattedMessage {...messages.addPackageTooltip} />
        </TooltipContent>
      </Tooltip>
      {isOpen && (
        <ModalPortal onClose={close}>
          <ModalComponent
            fulfillmentPlanId={isMultiPackageItemAllocationEnabled ? fulfillmentPlanId : undefined}
            hasInsuranceProviderSelected={hasInsuranceProviderSelected}
            hasMultiPackageSupport={supportsMultiPackage}
            initialPackages={orderPackages}
            onSave={onSave}
            shouldRequirePackageDescription={shouldRequirePackageDescription}
            items={isMultiPackageItemAllocationEnabled ? orderItems : undefined}
            packageItemAssignments={isMultiPackageItemAllocationEnabled ? packageItemAssignments : undefined}
            {...(isMultiPackageItemAllocationEnabled ? { suppressExtraEmptyPackage } : {})}
          />
        </ModalPortal>
      )}
    </>
  );
};

/**
 * @param {object} props
 * @param {string} [props.buttonClassName]
 * @param {string} [props.iconClassName]
 * @param {() => void} [props.onTrackOpenMultiPackageModal]
 * @returns {JSX.Element}
 */
export const MultiPackage = React.memo(MultiPackageComponent);
